import { useTranslation } from "react-i18next";

import { Section, SectionHeader, SectionTitle } from "@/blocks/Section";
import {
  UserName,
  UserNameContent,
  UserNameDetail,
  UserNameLabel,
} from "@/blocks/UserName";
import {
  categorizeSpeakers,
  deductMainSpeakers,
} from "@/helpers/participants/participants.helpers";

import { ParticipantProps } from "./SectionParticipants.decl";

import { SpeakerAvatar } from "@/components/SpeakerAvatar";
import { formatInternationalNumber } from "@/helpers/number/number.helpers";
import { formatInternational } from "@/helpers/phone_number/phone_number.helpers";
import { useCall } from "@/hooks/useCall/useCall";
import { VirtualAgent } from "../VirtualAgent/VirtualAgent";

function Participant({
  channel,
  text,
  isVirtualAgent,
  phoneNumber,
  fullName,
}: ParticipantProps) {
  if (isVirtualAgent) {
    return <VirtualAgent name={text ?? ""} />;
  }

  if (phoneNumber && fullName) {
    return (
      <UserName data-test="user">
        <SpeakerAvatar channel={channel} />
        <UserNameContent>
          <UserNameLabel data-test="user-name">{fullName}</UserNameLabel>
          <UserNameDetail>
            {formatInternationalNumber(phoneNumber)}
          </UserNameDetail>
        </UserNameContent>
      </UserName>
    );
  }

  return (
    <UserName data-test="user">
      <SpeakerAvatar channel={channel} />
      {text && <UserNameLabel data-test="user-name">{text}</UserNameLabel>}
    </UserName>
  );
}

export function SectionParticipants() {
  const { t } = useTranslation();
  const { call } = useCall();

  if (!call || !call.from || !call.to || !call.speakers) {
    return null;
  }

  const { mainAgentId, mainExternalNumber } = deductMainSpeakers({
    type: call.type,
    from: call.from,
    to: call.to,
  });

  const { mainAgent, externalUser, otherAgents, otherUsers } =
    categorizeSpeakers(call.speakers, mainAgentId, mainExternalNumber);

  return (
    <Section data-test="participants-section">
      <SectionHeader>
        <SectionTitle>{t("call_context.participants")}</SectionTitle>
      </SectionHeader>
      <div className="flex flex-col gap-2">
        {mainAgent && (
          <Participant
            isVirtualAgent={mainAgent.isVirtualAgent}
            channel={mainAgent.channel}
            text={
              mainAgent.fullName ?? formatInternational(mainAgent.number ?? "")
            }
          />
        )}
        {externalUser && (
          <Participant
            isVirtualAgent={externalUser.isVirtualAgent}
            channel={externalUser.channel}
            phoneNumber={externalUser.number}
            fullName={externalUser.fullName}
            text={
              externalUser.fullName ??
              formatInternational(externalUser.number ?? "")
            }
          />
        )}
        {otherAgents.length > 0 &&
          otherAgents.map((agent) => (
            <Participant
              isVirtualAgent={agent.isVirtualAgent}
              key={agent.channel}
              channel={agent.channel}
              text={agent.fullName ?? formatInternational(agent.number ?? "")}
            />
          ))}
        {otherUsers.length > 0 &&
          otherUsers.map((user) => (
            <Participant
              isVirtualAgent={user.isVirtualAgent}
              key={user.channel}
              channel={user.channel}
              text={user.fullName ?? formatInternational(user.number ?? "")}
            />
          ))}
      </div>
    </Section>
  );
}
